import { BettingCard } from './BettingCard';
import { CoachGradeCard } from './CoachGradeCard';
import { Disclosure } from './Disclosure';
import { FieldLines } from './Field';
import { EdgeChart } from './EdgeChart';
import { NarrativeCard } from './NarrativeCard';
import { ProbabilityBar, TeamChip } from './TeamSide';
import type { AnalysisResponse, Game, ReasoningStep } from '../lib/api';

type DecisionGrade = { decision: string; optimal: string | null; grade: 'Optimal' | 'Acceptable' | 'Questionable' | 'Wrong'; wp_cost_pct: number | null; epa_cost?: number | null; headline?: string; explanation?: string; what_the_numbers_say?: string[] };

const FACTOR_LABEL: Record<string, string> = {
  historical_head_to_head: 'Head to head', market_line: 'Market', situational_patterns: 'Situations', home_field: 'Home field',
  injuries: 'Injuries', weather: 'Weather', recent_form: 'Form', line_movement: 'Line movement', similar_games: 'Similar games', narratives: 'Storylines',
};

/** Which way a reasoning step leaned, read from its effect text. */
function lean(s: ReasoningStep): 'up' | 'down' | 'flat' {
  const t = `${s.effect ?? ''}`.toLowerCase();
  if (/no adjustment|neutral|no movement|no meaningful|cannot adjust|minimal/.test(t)) return 'flat';
  if (/up|push.*over|favor|support|positive|toward/.test(t) && !/down/.test(t)) return 'up';
  if (/down|reduc|negative|against|offset|discount/.test(t)) return 'down';
  return 'flat';
}

/** Reasoning steps as a strip of factor tiles; the full text opens per tile. */
export function FactorStrip({ steps }: { steps: ReasoningStep[] }) {
  return (
    <div>
      <p className="kicker mb-3">What moved the number</p>
      <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-4">
        {steps.map((s) => {
          const l = lean(s);
          return (
            <details key={s.step} className="card group p-4">
              <summary className="cursor-pointer list-none">
                <div className="flex items-center justify-between gap-2">
                  <span className="num text-[11px] text-faint">{String(s.step).padStart(2, '0')}</span>
                  <span className={`score text-[18px] leading-none ${l === 'up' ? 'text-green' : l === 'down' ? 'text-red' : 'text-faint'}`} aria-label={l}>{l === 'up' ? '+' : l === 'down' ? '-' : '='}</span>
                </div>
                <p className="mt-1 font-display text-[15px] font-bold capitalize leading-tight">{FACTOR_LABEL[s.factor] ?? s.factor.replace(/_/g, ' ')}</p>
                <p className="mt-1 line-clamp-2 text-[12px] text-muted group-open:line-clamp-none">{s.evidence}</p>
              </summary>
              <p className="mt-2 text-[12px] leading-relaxed text-ink">{s.effect}</p>
            </details>
          );
        })}
      </div>
    </div>
  );
}

function pWinner(r: AnalysisResponse, g?: Game): { pAway: number; pHome: number } | null {
  const c = r.prediction?.confidence;
  if (typeof c !== 'number') return null;
  const p = Math.max(0.5, Math.min(0.99, c / 100));
  const w = (r.prediction?.predicted_winner ?? '').toLowerCase();
  const homeWins = g ? (w.includes(g.home_team.name.toLowerCase()) || w.includes(g.home_team.abbreviation.toLowerCase())) : (r.home_team ?? '').toLowerCase() !== '' && w.includes((r.home_team ?? '').toLowerCase());
  return homeWins ? { pAway: 1 - p, pHome: p } : { pAway: p, pHome: 1 - p };
}

/** Renders whatever the router produced: a pick, a bet, a grade, a cause, or storylines. Visual first, text behind toggles. */
export function AnalysisResult({ r, game }: { r: AnalysisResponse; game?: Game }) {
  const home = r.home_team ?? game?.home_team.name ?? '', away = r.away_team ?? game?.away_team.name ?? '';
  const awaySide = game?.away_team ?? { name: away, abbreviation: away };
  const homeSide = game?.home_team ?? { name: home, abbreviation: home };
  const grade = (r as unknown as { decision_grade?: DecisionGrade }).decision_grade;
  const p = r.prediction;
  const probs = p ? pWinner(r, game) : null;
  return (
    <div className="stack animate-fadeIn">
      {p && (
        <>
          <div className="relative overflow-hidden bg-blue text-paper">
            <FieldLines />
            <div className="relative p-5 md:p-6">
              <p className="kicker !text-paper/80">Kova picks</p>
              <div className="mt-1 flex flex-wrap items-end justify-between gap-4">
                <p className="display text-[34px] leading-[0.9] md:text-[46px]">{p.predicted_winner}</p>
                {p.expected_margin && (
                  <p className="shrink-0 text-right">
                    <span className="score text-[34px] leading-none">{p.expected_margin.low}&ndash;{p.expected_margin.high}</span>
                    <span className="meta ml-2 !text-paper/80">margin</span>
                  </p>
                )}
              </div>
              {p.key_factors?.length > 0 && (
                <ol className="mt-4 grid grid-cols-1 md:grid-cols-3 md:gap-6">
                  {p.key_factors.slice(0, 3).map((f, i) => (
                    <li key={i} className="flex items-start gap-3 border-t border-white/30 py-3">
                      <span className="num pt-0.5 text-[12px] text-paper/70">{String(i + 1).padStart(2, '0')}</span>
                      <span className="text-[14px] leading-snug">{f}</span>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>
          {probs && <ProbabilityBar away={awaySide} home={homeSide} pAway={probs.pAway} pHome={probs.pHome} label={`Win probability  ${p.confidence_label ?? ''}`} />}
          {p.reasoning && <Disclosure title="Read the full analysis"><Paragraphs text={p.reasoning} />{p.betting_line_comparison && <p className="body-muted mt-3">{p.betting_line_comparison}</p>}</Disclosure>}
        </>
      )}
      {r.betting_recommendation && (
        <>
          <BettingCard rec={r.betting_recommendation} home={home} away={away} />
          {r.betting_recommendation.market_status === 'open' && <EdgeChart rec={r.betting_recommendation} home={home} away={away} />}
          {r.betting_recommendation.summary && <Disclosure title="Read the betting notes"><Paragraphs text={r.betting_recommendation.summary} /></Disclosure>}
        </>
      )}
      {r.causation && (
        <>
          <div className="relative overflow-hidden bg-ink text-paper">
            <FieldLines opacity={0.4} />
            <div className="relative p-5 md:p-6">
              <p className="kicker">What decided it</p>
              <p className="display mt-1 text-[24px] leading-[0.98] md:text-[32px]">{r.causation.answer}</p>
              {r.causation.turning_point && (
                <div className="mt-5 flex flex-wrap items-center gap-4 border-t border-white/25 pt-4">
                  <div className="shrink-0 border-r border-white/25 pr-4">
                    <p className="meta !text-paper/70">Turning point</p>
                    <p className="score text-[26px] leading-none">Q{r.causation.turning_point.quarter} <span className="num align-middle text-[16px]">{r.causation.turning_point.clock}</span></p>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[14px] leading-snug">{r.causation.turning_point.description}</p>
                    <p className="num mt-1 text-[14px] text-blue">{r.causation.turning_point.win_prob_swing}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <Cause title="Primary cause" c={r.causation.primary_cause} primary />
            {r.causation.contributing_causes.map((c, i) => <Cause key={i} title="Contributing" c={c} />)}
          </div>
          {r.causation.what_was_not_the_cause && <Disclosure title="What was not the cause"><p className="body-muted">{r.causation.what_was_not_the_cause}</p></Disclosure>}
        </>
      )}
      {r.storylines && r.storylines.length > 0 && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">{r.storylines.map((s, i) => <NarrativeCard key={i} s={s} />)}</div>
      )}
      {grade && <DecisionResult g={grade} />}
      {r.reasoning_steps && r.reasoning_steps.length > 0 && <FactorStrip steps={r.reasoning_steps} />}
      {game && (r.prediction || r.betting_recommendation) && (
        <p className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px] text-muted"><TeamChip t={game.away_team} size={18} /> at <TeamChip t={game.home_team} size={18} /></p>
      )}
      {r.sources_skipped ? <p className="label">{r.sources_skipped} source{r.sources_skipped > 1 ? 's' : ''} unavailable</p> : null}
    </div>
  );
}

function Cause({ title, c, primary = false }: { title: string; c: { cause: string; evidence: string; impact: string }; primary?: boolean }) {
  return (
    <div className={`card p-4 ${primary ? 'border-l-[3px] border-l-blue md:col-span-2' : ''}`}>
      <p className="kicker">{title}</p>
      <p className="mt-1 font-display text-[18px] font-bold leading-tight">{c.cause}</p>
      <p className="mt-1.5 text-[13px] text-muted">{c.evidence}</p>
      <p className="num mt-2 inline-block bg-blueTint px-2 py-1 text-[12px] text-blueDark">{c.impact}</p>
    </div>
  );
}

function DecisionResult({ g }: { g: DecisionGrade }) {
  return (
    <div className="stack">
      <CoachGradeCard d={{ category: 'decision', decision: g.decision, optimal: g.optimal, grade: g.grade, wp_impact_pct: g.wp_cost_pct !== null ? -g.wp_cost_pct : null, epa_cost: g.epa_cost ?? null, quarter: null, down_distance: null, yardline_100: null, score_differential: null, description: g.headline ?? null }} />
      {g.what_the_numbers_say && (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">{g.what_the_numbers_say.map((x, i) => <p key={i} className="card p-4 text-[14px]">{x}</p>)}</div>
      )}
      {g.explanation && <Disclosure title="Read the explanation"><Paragraphs text={g.explanation} /></Disclosure>}
    </div>
  );
}

/** Split long model text into short paragraphs, three sentences max each. */
export function Paragraphs({ text }: { text: string }) {
  const sentences = text.replace(/\s+/g, ' ').trim().split(/(?<=[.!?])\s+(?=[A-Z"(])/);
  const paras: string[] = [];
  for (let i = 0; i < sentences.length; i += 3) paras.push(sentences.slice(i, i + 3).join(' ').trim());
  return <div className="flex max-w-[68ch] flex-col gap-3 text-[15px] leading-relaxed text-muted">{paras.map((p, i) => <p key={i}>{p}</p>)}</div>;
}
